import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import UserContext from "./context/UserContext";
import { useNavigate, useLocation, Routes, Route } from "react-router-dom";
import { Sheet, List, ListItem, ListItemButton, Typography, Box, IconButton, Alert, Button, Chip } from '@mui/joy';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SchoolIcon from '@mui/icons-material/School';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import QuizIcon from '@mui/icons-material/Quiz';
import GradeIcon from '@mui/icons-material/Grade';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SmsIcon from '@mui/icons-material/Sms';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CampusConfirmation from './CampusConfirmation';
import StudentDetails from './students/StudentDetails';
import Profile from './students/Profile';
import PlacementProfile from './students/PlacementProfile';
import TestSchedule from './students/TestSchedule';
import TestMarks from './students/TestMarks';
import AssessmentStatus from './students/AssessmentStatus';
import SMSHistory from './students/SMSHistory';
import Support from './students/Support';
import Attendance from './students/Attendance';
import Projects from './students/Projects';
import axiosConfig from './util/axiosConfig';

const Home = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logoutUser, isLoading } = useContext(UserContext);
    const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
    const [projectChatUnread, setProjectChatUnread] = useState({ totalUnread: 0, chats: [] });
    const [projectChatNotification, setProjectChatNotification] = useState(null);
    const previousUnreadTotalRef = useRef(0);

    useEffect(() => {
        if (!isLoading && !user) {
            navigate('/login');
        }
    }, [user, isLoading, navigate]);

    const handleLogout = async () => {
        try {
            await logoutUser();
            navigate("/login");
        } catch (error) {
            console.error("Logout failed:", error);
            navigate("/login");
        }
    };

    const fetchUnreadProjectChats = useCallback(async ({ showNotification = false } = {}) => {
        try {
            const response = await axiosConfig.get('/students/projects/messages/unread');
            const unreadData = {
                totalUnread: response.data.totalUnread || 0,
                chats: response.data.chats || []
            };

            setProjectChatUnread(unreadData);

            if (unreadData.totalUnread === 0) {
                setProjectChatNotification(null);
            }

            if (showNotification && unreadData.totalUnread > previousUnreadTotalRef.current && unreadData.chats.length > 0) {
                setProjectChatNotification(unreadData.chats[0]);
            }

            previousUnreadTotalRef.current = unreadData.totalUnread;
        } catch (error) {
            console.error('Error fetching unread project chats:', error);
        }
    }, []);

    useEffect(() => {
        if (isLoading || !user) {
            if (!isLoading && !user) {
                setProjectChatUnread({ totalUnread: 0, chats: [] });
                setProjectChatNotification(null);
                previousUnreadTotalRef.current = 0;
            }
            return undefined;
        }

        fetchUnreadProjectChats({ showNotification: true });

        const intervalId = window.setInterval(() => {
            fetchUnreadProjectChats({ showNotification: true });
        }, 60000);

        return () => window.clearInterval(intervalId);
    }, [isLoading, user, fetchUnreadProjectChats]);

    const openProjectChat = (assignmentId) => {
        setProjectChatNotification(null);
        navigate(`/projects/${assignmentId}?focus=chat`);
    };

    const menuItems = [
        {
            title: 'Home',
            path: '/',
            icon: <HomeIcon />,
            color: 'primary'
        },
        {
            title: 'Profile',
            path: '/profile',
            icon: <AccountCircleIcon />,
            color: 'primary'
        },
        {
            title: 'Placement Profile',
            path: '/placement-profile',
            icon: <BusinessCenterIcon />,
            color: 'primary'
        },
        {
            title: 'Tests Schedule',
            path: '/tests-schedule',
            icon: <QuizIcon />,
            color: 'primary'
        },
        {
            title: 'Test Marks',
            path: '/test-marks',
            icon: <GradeIcon />,
            color: 'primary'
        },
        {
            title: 'Projects',
            path: '/projects',
            icon: <AssignmentIcon />,
            color: 'primary'
        },
        {
            title: 'Attendance',
            path: '/attendance',
            icon: <EventAvailableIcon />,
            color: 'primary'
        },
        {
            title: 'Campus Confirmation',
            path: '/campus-confirmation',
            icon: <SchoolIcon />,
            color: 'primary'
        },
        {
            title: 'SMS History',
            path: '/sms-history',
            icon: <SmsIcon />,
            color: 'primary'
        },
        {
            title: 'Assessment Status',
            path: '/assessment-status',
            icon: <AssessmentIcon />,
            color: 'primary'
        },
        {
            title: 'Support',
            path: '/support',
            icon: <SupportAgentIcon />,
            color: 'primary'
        },
        {
            title: 'Logout',
            path: '/logout',
            icon: <LogoutIcon />,
            action: handleLogout,
            color: 'danger'
        }
    ];

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flex: 1,
                width: '100%',
                overflow: 'hidden',
                m: 0,
                p: 0,
            }}
        >
            {/* Left Menu */}
            <Sheet
                sx={{
                    width: isMenuCollapsed ? '50px' : '250px',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    p: '5px',
                    m: 0,
                    borderRadius: 0,
                    transition: 'width 0.2s ease',
                    position: 'relative',
                    height: '100%',
                    overflow: 'auto'
                }}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isMenuCollapsed ? 'center' : 'space-between',
                    mb: '5px'
                }}>
                    {!isMenuCollapsed && (
                        <Typography level="title-lg" sx={{ p: 0 }}>
                            Vector Student Portal
                        </Typography>
                    )}
                    <IconButton
                        variant="plain"
                        color="neutral"
                        onClick={() => setIsMenuCollapsed(!isMenuCollapsed)}
                        sx={{
                            minWidth: '32px',
                            p: '5px',
                        }}
                    >
                        {isMenuCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </Box>

                <List
                    size="sm"
                    sx={{
                        '--ListItem-radius': '4px',
                        p: 0,
                        m: 0,
                    }}
                >
                    {menuItems.map((item) => (
                        <ListItem key={item.path} sx={{ p: 0, m: 0 }}>
                            <ListItemButton
                                selected={item.path === '/' ? location.pathname === '/' : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
                                onClick={item.action || (() => navigate(item.path))}
                                sx={{
                                    p: '5px',
                                    m: 0,
                                    gap: '5px',
                                    justifyContent: isMenuCollapsed ? 'center' : 'flex-start',
                                    transition: 'background-color 0.2s ease',
                                    '&.Mui-selected': {
                                        backgroundColor: '#ea7a23 !important',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#ea7a23 !important',
                                        }
                                    },
                                    '&:not(.Mui-selected):hover': {
                                        backgroundColor: '#ffd4b3',
                                    },
                                    color: item.color === 'danger' ? 'danger.plainColor' : undefined,
                                    ...(item.color === 'danger' && {
                                        '&:hover': {
                                            backgroundColor: 'danger.softBg',
                                        }
                                    })
                                }}
                                title={isMenuCollapsed ? item.title : undefined}
                            >
                                {item.icon}
                                {!isMenuCollapsed && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                        <Typography
                                            level="title-sm"
                                            sx={{
                                                m: 0,
                                                p: 0,
                                                color: 'inherit'
                                            }}
                                        >
                                            {item.title}
                                        </Typography>
                                        {item.path === '/projects' && projectChatUnread.totalUnread > 0 && (
                                            <Chip color="danger" size="sm" variant="solid">
                                                {projectChatUnread.totalUnread > 99 ? '99+' : projectChatUnread.totalUnread}
                                            </Chip>
                                        )}
                                    </Box>
                                )}
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Sheet>

            {/* Main Content */}
            <Box
                sx={{
                    flex: 1,
                    p: '5px',
                    m: 0,
                    overflow: 'auto',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Routes>
                    <Route
                        path="/"
                        element={
                            <Box>
                                <Typography level="h3" sx={{ textAlign: 'center', mt: '5px', p: 0, m: 0, mb: 3 }}>
                                    {user?.name ? `${user?.name} Dashboard` : 'Student Dashboard'}
                                </Typography>
                                <StudentDetails />
                            </Box>
                        }
                    />
                    <Route
                        path="/profile"
                        element={<Profile />}
                    />
                    <Route
                        path="/placement-profile"
                        element={<PlacementProfile />}
                    />
                    <Route
                        path="/tests-schedule"
                        element={<TestSchedule />}
                    />
                    <Route
                        path="/test-marks"
                        element={<TestMarks />}
                    />
                    <Route
                        path="/attendance"
                        element={<Attendance />}
                    />
                    <Route
                        path="/campus-confirmation"
                        element={<CampusConfirmation />}
                    />
                    <Route
                        path="/sms-history"
                        element={<SMSHistory />}
                    />
                    <Route
                        path="/assessment-status"
                        element={<AssessmentStatus />}
                    />
                    <Route
                        path="/support"
                        element={<Support />}
                    />
                    <Route
                        path="/projects"
                        element={<Projects onProjectMessagesRead={fetchUnreadProjectChats} />}
                    />
                    <Route
                        path="/projects/:assignmentId"
                        element={<Projects onProjectMessagesRead={fetchUnreadProjectChats} />}
                    />
                </Routes>
            </Box>

            {projectChatNotification && (
                <Alert
                    color="warning"
                    variant="soft"
                    sx={{
                        position: 'fixed',
                        top: 16,
                        right: 16,
                        zIndex: 1500,
                        maxWidth: '360px',
                        boxShadow: 'lg',
                        alignItems: 'flex-start'
                    }}
                    endDecorator={
                        <Button size="sm" color="warning" onClick={() => openProjectChat(projectChatNotification.assignmentId)}>
                            Open Chat
                        </Button>
                    }
                >
                    <Box>
                        <Typography level="title-sm">New project chat message</Typography>
                        <Typography level="body-sm">
                            {projectChatNotification.title}
                        </Typography>
                        {projectChatNotification.latestMessage && (
                            <Typography level="body-xs" sx={{ mt: 0.5 }}>
                                {projectChatNotification.latestSenderName}: {projectChatNotification.latestMessage}
                            </Typography>
                        )}
                    </Box>
                </Alert>
            )}
        </Box>
    );
};

export default Home; 